import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getSectionSocialLogoSrc } from "@/assets/images/sections";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import { ButtonWithLink } from "@/components/withLink";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Link } from "@/router";
import type { Section } from "@/types";

import { sectionCatalogSubhead } from "../_utils/formatSectionLegacyId";

const VISIBLE_OFFSET = 4;
const CARD_WIDTH = 200;
const CARD_HEIGHT = 260;
const MOBILE_CARD_WIDTH = 240;
const MOBILE_CARD_HEIGHT = 300;
const WHEEL_STEP_THRESHOLD = 48;
const SWIPE_THRESHOLD_PX = 48;

type Props = {
  sections: Section[];
};

function cardTransform(
  offset: number,
  cardWidth: number,
  isMobile: boolean
): { transform: string; opacity: number; zIndex: number } {
  if (isMobile) {
    return {
      transform: "translate(-50%, -50%)",
      opacity: offset === 0 ? 1 : 0,
      zIndex: offset === 0 ? 100 : 0
    };
  }

  const abs = Math.abs(offset);
  const translateX = offset * (cardWidth * 0.62);
  const translateZ = -abs * 72;
  const rotateY = offset * -42;
  const scale = Math.max(0.62, 1 - abs * 0.11);
  const opacity = Math.max(0.35, 1 - abs * 0.18);

  return {
    transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
    opacity,
    zIndex: 100 - abs
  };
}

export default function SectionCoverFlow({ sections }: Props) {
  const isMobile = useIsMobile();
  const cardWidth = isMobile ? MOBILE_CARD_WIDTH : CARD_WIDTH;
  const cardHeight = isMobile ? MOBILE_CARD_HEIGHT : CARD_HEIGHT;
  const [activeIndex, setActiveIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const carouselRef = useRef<HTMLDivElement>(null);
  const wheelAccumulatorRef = useRef(0);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  const activeSection = sections[activeIndex];
  const sectionIdsKey = sections.map((section) => section.id).join("|");

  useEffect(() => {
    setActiveIndex(0);
    wheelAccumulatorRef.current = 0;
  }, [sectionIdsKey]);

  useLayoutEffect(() => {
    let frame = 0;
    const focusCarousel = () => {
      carouselRef.current?.focus({ preventScroll: true });
    };

    // Defer past the Browse/List toggle click so focus is not left on the segment button.
    frame = requestAnimationFrame(() => {
      requestAnimationFrame(focusCarousel);
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  const move = useCallback(
    (delta: number) => {
      if (sections.length === 0) return;
      setActiveIndex((current) => (current + delta + sections.length) % sections.length);
    },
    [sections.length]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        move(1);
      }
    },
    [move]
  );

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || sections.length <= 1) return;

    const onWheel = (event: WheelEvent) => {
      if (!carousel.contains(document.activeElement)) return;

      const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const delta = isHorizontal ? event.deltaX : event.deltaY;
      if (delta === 0) return;

      event.preventDefault();
      wheelAccumulatorRef.current += delta;

      if (wheelAccumulatorRef.current >= WHEEL_STEP_THRESHOLD) {
        wheelAccumulatorRef.current = 0;
        move(1);
      } else if (wheelAccumulatorRef.current <= -WHEEL_STEP_THRESHOLD) {
        wheelAccumulatorRef.current = 0;
        move(-1);
      }
    };

    carousel.addEventListener("wheel", onWheel, { passive: false });
    return () => carousel.removeEventListener("wheel", onWheel);
  }, [move, sections.length]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    carouselRef.current?.focus({ preventScroll: true });
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start) return;

      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      if (Math.abs(deltaX) < Math.abs(deltaY) || Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;

      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);

      if (deltaX < 0) move(1);
      else move(-1);
    },
    [move]
  );

  const visibleCards = useMemo(() => {
    if (isMobile) {
      const section = sections[activeIndex];
      return section ? [{ section, index: activeIndex }] : [];
    }

    return sections
      .map((section, index) => ({ section, index }))
      .filter(({ index }) => Math.abs(index - activeIndex) <= VISIBLE_OFFSET);
  }, [activeIndex, isMobile, sections]);

  if (!activeSection) return null;

  const subhead = sectionCatalogSubhead(activeSection.legacyId);

  return (
    <div className="flex flex-col gap-4 sm:gap-8" data-testid="sections-catalog-browse">
      <div className="relative">
        <div
          ref={carouselRef}
          className="relative mx-auto h-[19rem] max-w-5xl touch-pan-y overflow-hidden rounded-xl border border-sys-brand-outline-variant bg-gradient-to-b from-sys-brand-surface-container-low to-sys-brand-surface sm:h-[22rem] sm:rounded-2xl"
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          role="region"
          aria-label="Section browse carousel"
          tabIndex={0}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.06)_0%,_transparent_70%)]"
            aria-hidden
          />

          <div className="absolute inset-y-0 left-2 z-[120] hidden items-center sm:flex">
            <IconButton
              name="chevron_left"
              ariaLabel="Previous Section"
              variant="filledTonal"
              onClick={() => move(-1)}
              disabled={sections.length <= 1}
            />
          </div>
          <div className="absolute inset-y-0 right-2 z-[120] hidden items-center sm:flex">
            <IconButton
              name="chevron_right"
              ariaLabel="Next Section"
              variant="filledTonal"
              onClick={() => move(1)}
              disabled={sections.length <= 1}
            />
          </div>

          <div className="relative h-full w-full" style={isMobile ? undefined : { perspective: "1200px" }}>
            <div className="relative h-full w-full" style={isMobile ? undefined : { transformStyle: "preserve-3d" }}>
              {visibleCards.map(({ section, index }) => {
                const offset = index - activeIndex;
                const { transform, opacity, zIndex } = cardTransform(offset, cardWidth, isMobile);
                const logoUrl = getSectionSocialLogoSrc(section.legacyId);
                const isActive = offset === 0;

                return (
                  <Link
                    key={section.id}
                    to="/sections/:sectionId"
                    params={{ sectionId: section.id }}
                    className="absolute left-1/2 top-1/2 block will-change-transform"
                    style={{
                      width: cardWidth,
                      height: cardHeight,
                      transform,
                      opacity,
                      zIndex,
                      transition: reduceMotion
                        ? "none"
                        : isMobile
                          ? "opacity 200ms ease"
                          : "transform 450ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease"
                    }}
                    data-testid={`section-cover-card-${section.id}`}
                    aria-label={`Open ${section.name}`}
                    onClick={(event) => {
                      if (suppressClickRef.current) {
                        event.preventDefault();
                        return;
                      }
                      if (!isActive) {
                        event.preventDefault();
                        setActiveIndex(index);
                      }
                    }}
                  >
                    <div
                      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-sys-brand-surface shadow-web-2 ${
                        isActive
                          ? "border-sys-brand-primary ring-2 ring-sys-brand-primary/30"
                          : "border-sys-brand-outline-variant"
                      }`}
                    >
                      <div className="flex flex-1 items-center justify-center border-b border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-6">
                        {logoUrl ? (
                          <img src={logoUrl} alt="" className="max-h-28 max-w-[10rem] object-contain drop-shadow-sm" />
                        ) : (
                          <Icon name="map" size={48} className="text-sys-brand-on-surface-variant" />
                        )}
                      </div>
                      <div className="px-4 py-3 text-center">
                        <div className="line-clamp-2 text-title-small text-sys-brand-on-surface">{section.name}</div>
                        {sectionCatalogSubhead(section.legacyId) && (
                          <div className="mt-1 text-body-small text-sys-brand-on-surface-variant">
                            {sectionCatalogSubhead(section.legacyId)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {isMobile && (
          <div className="mt-3 flex items-center justify-center gap-6">
            <IconButton
              name="chevron_left"
              ariaLabel="Previous Section"
              variant="filledTonal"
              onClick={() => move(-1)}
              disabled={sections.length <= 1}
            />
            <div className="min-w-[5rem] text-center text-body-small text-sys-brand-on-surface-variant">
              {activeIndex + 1} of {sections.length}
            </div>
            <IconButton
              name="chevron_right"
              ariaLabel="Next Section"
              variant="filledTonal"
              onClick={() => move(1)}
              disabled={sections.length <= 1}
            />
          </div>
        )}

        <div className="mx-auto mt-4 flex max-w-md flex-col items-center gap-4 text-center sm:mt-8">
          <div>
            <div className="text-title-large text-sys-brand-on-surface sm:text-headline-small">
              {activeSection.name}
            </div>
            {subhead && <div className="mt-1 text-body-medium text-sys-brand-on-surface-variant">{subhead}</div>}
          </div>
          <ButtonWithLink
            link={{ to: "/sections/:sectionId", params: { sectionId: activeSection.id } }}
            variant="filled"
            icon="arrow_forward"
            trailingIcon
          >
            Open Section
          </ButtonWithLink>
          {!isMobile && (
            <div className="text-body-small text-sys-brand-on-surface-variant">
              {activeIndex + 1} of {sections.length}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
